<template>
  <view-wrapper
    name="Import History"
    :loading="loading"
    :loadingText="loadingText"
  >
    <wrapper>
      <filter-and-sort-box
        :data="filteredData"
        :headers="headers"
        :useSearch="false"
        :columnsToNotSort="columnsToNotSort"
      >
        <template v-slot:rows="{ rows }">
          <tr class="bg-primary" v-for="(d, index) in rows" :key="index">
            <td
              class="relative py-1"
              :key="key + index1"
              v-for="(key, index1) in Object.keys(headers)"
            >
              <div
                class="w-full pl-4 bg-primary-light"
                :class="{
                  'py-4': !isLast(headers, index1),
                  'icon-spacing': isLast(headers, index1)
                }"
              >
                <span v-if="!isLast(headers, index1)">{{ d[key] }}</span>
                <icon-button
                  v-if="d['fileName'] && isLast(headers, index1)"
                  tooltip="Download File"
                  @click="downloadFile(d)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-6 h-6 mr-2 icon-spacing-svg text-highlight"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </icon-button>
                <icon-button
                  v-if="isLast(headers, index1)"
                  tooltip="Delete import"
                  @click="importToDelete = d"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </icon-button>
              </div>
            </td>
            <td class="p-2"></td>
          </tr>
        </template>
      </filter-and-sort-box>
      <div class="py-2">
        <div class="float-right">
          <button
            class="text-base bg-transparent border-none disabled:cursor-not-allowed"
            @click="setPage(page - 1)"
            :disabled="page === 1"
          >
            <i class="arrow left" :class="{ 'border-gray': page === 1 }" />
          </button>
          <span class="select-none">Page {{ page }}</span>
          <button
            class="text-base bg-transparent border-none disabled:cursor-not-allowed"
            @click="setPage(page + 1)"
            :disabled="importHistory.length < pageSize"
          >
            <i
              class="arrow right"
              :class="{ 'border-gray': importHistory.length < pageSize }"
            />
          </button>
        </div>
      </div>
    </wrapper>

    <modal
      v-if="importToDelete"
      type="warning"
      heading="Confirm delete"
      :description="importToDelete.name"
      @close="importToDelete = null"
    >
      <template slot="buttons">
        <grafokett-button
          class="mb-2 button big primary white"
          @click="doDeleteImport(importToDelete)"
          >OK</grafokett-button
        >
        <grafokett-button
          class="mb-2 button big primary white"
          @click="importToDelete = null"
          >Cancel</grafokett-button
        >
      </template>
    </modal>

    <modal
      v-if="processInfo && processInfo.heading"
      :heading="processInfo.heading"
      :description="processInfo.description"
      @close="processInfo = null"
    />

    <modal
      v-if="
        deleteImportError &&
          deleteImportError.errors &&
          deleteImportError.length
      "
      type="error"
      heading="An error occured"
      :description="deleteImportError.errors[0].message"
      @close="deleteImportError = null"
    >
      <template slot="buttons">
        <grafokett-button
          class="mb-2 button big primary white"
          @click="deleteImportError = null"
          >Close</grafokett-button
        >
      </template>
    </modal>
  </view-wrapper>
</template>

<script>
import { BehaviorSubject, combineLatest } from "rxjs";
import FilterAndSortBox from "../components/FilterAndSortBox";
import IconButton from "../components/IconButton.vue";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import Modal from "../components/Modal";
import GrafokettButton from "../components/forms/Button";
import { formatDate } from "../utils/dateHelpers";

export default {
  components: {
    FilterAndSortBox,
    IconButton,
    Modal,
    GrafokettButton
  },
  data() {
    return {
      loading: false,
      loadingText: "Loading Import History",
      importHistory: [],
      importToDelete: null,
      deleteImportError: null,
      processInfo: null,
      headers: {
        name: "Name",
        timestamp: "Timestamp",
        download: null
      },
      columnsToNotSort: ["download"],
      searchText: "",
      page: 1,
      pageSize: 10,
      pageNumber$: null,
      pageSize$: null
    };
  },
  methods: {
    async getHistory(page, pageSize) {
      this.loading = true;

      const account = this.$store.getters["AUTH/AUTH_GET_USER"];

      const { data } = await this.$store.$api.get(
        `/api/importhistory/?offset=${pageSize * (page - 1)}&take=${pageSize}${
          !account.UserRole.includes("Administrator")
            ? "&userId=" + account.UserId
            : ""
        }`
      );
      this.importHistory = data.data.map(x => ({
        ...x,
        timestamp: formatDate(x.timestamp)
      }));
      this.loading = false;
    },
    async downloadFile(entry) {
      try {
        const { data } = await this.$store.$api.get(
          `/api/importhistory/${entry.importDataId}/download`,
          {
            responseType: "blob"
          }
        );

        const link = document.createElement("a");
        link.href = URL.createObjectURL(new Blob([data]));
        link.download = entry.fileName;
        document.body.append(link);
        link.click();
        link.remove();

        setTimeout(() => URL.revokeObjectURL(link.href), 7000);
      } catch (error) {
        throw error;
      }
    },
    setPage(newPage) {
      this.pageNumber$.next(Math.max(1, newPage));
    },
    isLast(headers, index) {
      return Object.keys(headers).length - 1 === index;
    },
    async doDeleteImport(importToDelete) {
      this.processInfo = {
        heading: "Deleting import",
        description: "Removing the import data with related orders and articles"
      };

      const deleteResult = await this.$store.dispatch(
        "IMPORTS/DELETE",
        importToDelete.id
      );
      this.deleteImportError = deleteResult.error;
      this.processInfo = null;

      if (this.deleteImportError) return;

      this.importToDelete = null;
      this.importHistory = [
        ...this.importHistory.filter(x => x.id !== importToDelete.id)
      ];
    }
  },
  computed: {
    filteredData() {
      return this.importHistory;
    }
  },
  async mounted() {
    this.pageNumber$ = new BehaviorSubject(1);
    this.pageNumber$$ = this.pageNumber$.subscribe(
      value => (this.page = value)
    );

    this.pageSize$ = new BehaviorSubject(10);

    this.searchStream$$ = combineLatest(
      this.pageNumber$.pipe(
        debounceTime(300),
        distinctUntilChanged()
      ),
      this.pageSize$
    )
      .pipe(debounceTime(300))
      .subscribe(([pageNumber, pageSize]) => {
        this.getHistory(pageNumber, pageSize);
      });
  },
  beforeDestroy() {
    this.pageNumber$$.unsubscribe();
    this.searchStream$$.unsubscribe();
  }
};
</script>

<style>
.icon-spacing {
  height: 52px;
}

.icon-spacing-svg {
  margin-top: 14px;
}
</style>
